/*
  原生 App（Capacitor）與網站之間的橋接。

  ## 為什麼 Google 登入要另外處理

  App 的畫面其實是載入網站（capacitor.config.json 的 server.url）。Google 禁止在
  App 內嵌的網頁視窗裡登入（會顯示 disallowed_useragent），而 Capacitor 遇到
  不在 allowNavigation 裡的網址會丟給外部 Chrome —— 結果就是在 Chrome 裡登入完、
  停在 Chrome，App 這邊還是沒登入。

  改成：
    1. App 裡按 Google 登入 → 用系統瀏覽器分頁（Chrome Custom Tabs）開 Google，
       並告訴後端「這次從 App 來」（googleLogin?app=true）
    2. 後端登入完成後導到 https://<網站>/app-return/login?guid=...
    3. 這個網址是 App Link（AndroidManifest.xml），Android 直接交給 App，分頁跟著關掉
    4. App 收到後把網頁導到 /loginSuccess?guid=...，接著走原本「用 guid 換憑證」的流程

  App Link 沒通過驗證、或瀏覽器沒有自動交給 App 時，/app-return/ 那一頁
  （views/AppReturn.vue）會顯示「回到小芋頭 App」按鈕當備援。

  付款與站外連結不走這裡，由原生的 NavigationPolicyPlugin 決定在哪裡開
  （android/app/src/main/java/com/kingfong/taro/NavigationPolicyPlugin.java）：
  付款留在 App 內、站外連結用 App 內瀏覽器分頁、LINE 之類交給對應的 App。
*/
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";

export const APP_RETURN_PREFIX = "/app-return/";

/** 與 capacitor.config.json 的 appId 相同，備援按鈕用它指定開哪個 App */
export const APP_PACKAGE = "com.kingfong.taro";

export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

/**
 * 能不能走「系統瀏覽器分頁 + App Link」的登入。
 *
 * 網站一更新，還沒更新的舊版 App 也會載入新網站，但舊版沒有瀏覽器分頁外掛 ——
 * 直接呼叫會失敗、按鈕沒反應。舊版就照舊整頁跳轉（也不告訴後端從 App 來），
 * 跟改版前一樣。
 */
export function canUseAppAuth() {
  return isNativeApp() && Capacitor.isPluginAvailable("Browser");
}

/**
 * 打開 Google 的登入頁。App 裡用系統瀏覽器分頁，網頁照舊整頁跳轉。
 * 回傳的 Promise 在分頁打開（App）或開始跳轉（網頁）後完成。
 */
export function openGoogleAuth(url) {
  if (canUseAppAuth()) return Browser.open({ url });
  location.replace(url);
  return Promise.resolve();
}

/**
 * 把 /app-return/ 的網址轉成 App 裡要前往的站內路由；不是 /app-return/ 的回 null。
 *
 *   /app-return/login?guid=...   登入成功 → /loginSuccess?guid=...
 *   /app-return/signin           登入失敗或取消 → 登入頁
 */
export function resolveAppReturn(urlString) {
  let url;
  try {
    url = new URL(urlString, location.href);
  } catch {
    return null;
  }
  if (!url.pathname.startsWith(APP_RETURN_PREFIX)) return null;

  const action = url.pathname.slice(APP_RETURN_PREFIX.length).replace(/\/+$/, "");
  const guid = url.searchParams.get("guid");

  if (action === "login" && guid) return { path: "/loginSuccess", query: { guid } };
  return { path: "/signin" };
}

/**
 * 分享連結（商品頁、升級邀請、自訂頁…）轉成 App 裡要前往的站內路徑；不是這個網站的回 null。
 *
 * 哪些網址會交給 App 由 AndroidManifest.xml 的 App Link 決定，這裡不再列一次 ——
 * 只要是 App 目前載入的網站（正式版 App 是正式站、測試版是測試站），就導到同一頁，
 * 查詢參數照帶（推薦碼 refc 靠它）。
 */
export function resolveSiteLink(urlString) {
  let url;
  try {
    url = new URL(urlString, location.href);
  } catch {
    return null;
  }
  if (url.host !== location.host) return null;
  return url.pathname + url.search + url.hash;
}

/**
 * 從 App 外面（瀏覽器）叫起 App 並帶著目前這個 /app-return/ 網址的連結。
 * 指定 package，所以就算 App Link 沒通過驗證也會交給這個 App。
 * fallbackUrl：沒裝 App 時改開的網址。
 */
export function buildAppIntentUrl(fallbackUrl) {
  const { host, pathname, search } = window.location;
  let intent = `intent://${host}${pathname}${search}#Intent;scheme=https;package=${APP_PACKAGE};`;
  if (fallbackUrl) intent += `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};`;
  return intent + "end";
}

/** App 啟動時呼叫一次：接住 App Link，導到對應頁面 */
export function initNativeApp(router) {
  if (!isNativeApp()) return;

  const handle = (url) => {
    const target = resolveAppReturn(url);
    if (target) {
      // Android 上 App 被叫到前景時分頁會自己關；iOS 要手動關。沒有開著的分頁時會失敗，不管它
      Browser.close().catch(() => {});
      router.replace(target).catch(() => {});
      return;
    }

    // 分享連結：用 push，按返回可以回到原本在看的頁面
    const path = resolveSiteLink(url);
    if (path && path !== router.currentRoute.fullPath) router.push(path).catch(() => {});
  };

  App.addListener("appUrlOpen", (event) => handle(event.url));

  // 冷啟動：在瀏覽器分頁登入的期間 App 被系統回收、或 App 沒開著時點了分享連結，由 App Link 叫起來
  App.getLaunchUrl()
    .then((result) => result && result.url && handle(result.url))
    .catch(() => {});
}
